import { FC, forwardRef, useState } from "react";

import ProceduresOverview from "../Procedures/ProceduresOverviewDashboard";
import CloseIcon from "@mui/icons-material/Close";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Slide,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { Translate } from "@/components/Application/Translate";
import EditProcedureFolder from "@/components/Procedures/EditProcedureFolder";
import NewProcedureFolder from "@/components/Procedures/NewProcedureFolder";
import ProceduresActions from "@/components/Procedures/ProceduresActions";

/* ────────────────────────────────────────────────────────── */
/* Header (left column)                                       */
/* ────────────────────────────────────────────────────────── */

interface IWrapperHeadingLeft {
	handleToggleAll: () => void;
	expandAll: boolean;
}

const Transition = forwardRef(function Transition(props: any, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

export const WrapperHeadingLeft: FC<IWrapperHeadingLeft> = ({
	handleToggleAll,
	expandAll,
}) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [overviewOpen, setOverviewOpen] = useState(false);

	return (
		<div
			className="heading-wrapper"
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: 12,
			}}
		>
			<div className="heading-wrapper-left" style={{ minWidth: 0 }}>
				<Typography variant="h4" component="h1" sx={{ fontWeight: 550 }}>
					<Translate className="heading" String="Breadcrumbs.Link.Procedures">
						Procedures
					</Translate>
				</Typography>
			</div>

			<div
				className="heading-wrapper-right"
				style={{ display: "flex", gap: 8 }}
			>
				{/* Overview button (visible to everyone) */}
				{/* <Button
					variant="outlined"
					color="primary"
					startIcon={<InsightsOutlinedIcon />}
					onClick={() => setOverviewOpen(true)}
					sx={{
						borderRadius: 2,
						textTransform: "none",
						fontWeight: 800,
					}}
				>
					<Translate>Overview</Translate>
				</Button> */}

				{/* Existing admin actions */}
				<AdminProtected>
					<ProceduresActions handleToggleAll={handleToggleAll} />
				</AdminProtected>
			</div>

			{/* Overview dialog */}
			<Dialog
				open={overviewOpen}
				onClose={() => setOverviewOpen(false)}
				fullScreen={fullScreen}
				maxWidth="xl"
				fullWidth
				TransitionComponent={Transition}
				PaperProps={{
					sx: {
						borderRadius: fullScreen ? 0 : 2,
					},
				}}
			>
				<DialogTitle
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						pr: 1,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<InsightsOutlinedIcon />
						<Typography variant="h6" fontWeight={800}>
							<Translate>Procedures overview</Translate>
						</Typography>
					</Box>

					<IconButton
						onClick={() => setOverviewOpen(false)}
						aria-label="Close"
						size="small"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers sx={{ p: { xs: 1.5, md: 2 } }}>
					{/* Show the dashboard inside the dialog */}
					<Paper
						elevation={0}
						sx={{
							p: { xs: 1, md: 2 },
							border: (t) => `1px solid ${t.palette.divider}`,
							borderRadius: 2,
						}}
					>
						{/* Default to 1 year view per your latest spec */}
						<ProceduresOverview initialRangeDays={365} />
					</Paper>
				</DialogContent>

				<DialogActions
					sx={{
						px: 2,
						py: 1,
						justifyContent: "flex-end",
					}}
				>
					<Button
						onClick={() => setOverviewOpen(false)}
						variant="outlined"
						sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
					>
						<Translate>Close</Translate>
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

/* ────────────────────────────────────────────────────────── */
/* Keep your folder dialogs as-is                             */
/* ────────────────────────────────────────────────────────── */

export interface NewFolderProps {
	open: boolean;
	onClose: () => void;
	groupId?: any;
	isLink: boolean;
}

export function NewFolder(props: NewFolderProps) {
	const { onClose, open, isLink } = props;
	return (
		<NewProcedureFolder isNewLink={isLink} handleClose={onClose} open={open} />
	);
}

export interface EditFolderProps {
	open: boolean;
	onClose: () => void;
	groupId?: any;
	isLink: boolean;
}

export function EditFolder(props: EditFolderProps) {
	const { onClose, open, groupId, isLink } = props;
	return (
		<EditProcedureFolder
			isLink={isLink}
			handleClose={onClose}
			open={open}
			id={groupId}
		/>
	);
}
